// Estado falso do delegation log (modo fake, só em dev — Parte 0.6; GIY-075; Parte 5.2/5.5/5.7; REQ-AUTHZ-006..008/010/022): o
// diretório de estudos no centro (o seam `IStudySiteDirectory` do servidor, que o M10 implementa), o log de cada estudo, as delegações
// com a máquina de estados, as tentativas bloqueadas pelas travas e o catálogo de tarefas (Anexo A.4, códigos do Anexo E.3). As travas
// (vínculo, certificação, licença, função × tarefa, skill — OPEN-110) são julgadas aqui contra o estado falso de Pessoas, na mesma ordem
// e com os mesmos códigos do servidor (`DelegationLockRules`).
import type { MeResponse, components } from '@giyus/api-client';

import { FIXTURE_DELEGATION, FIXTURE_IDS } from '../auth/me.fixture';
import { shiftDays, todayUtc } from './authzStore';
import {
  FUNCTIONS,
  type FakePeopleState,
  type FakePerson,
  PEOPLE_IDS,
  type Seeder,
  displayNameOf,
  linkStatusOn,
  peopleId,
  seedCertification,
  seedLicense,
  seedLink,
  seedPerson,
} from './peopleStore';

type Schemas = components['schemas'];
export type DelegationResponse = Schemas['AuthorizationDelegationResponse'];
export type DelegationLogResponse = Schemas['AuthorizationDelegationLogResponse'];
export type DelegationTaskTypeResponse = Schemas['AuthorizationDelegationTaskTypeResponse'];
export type DelegationBlockAttemptResponse = Schemas['AuthorizationDelegationBlockAttemptResponse'];
export type DelegationBlockReasonResponse = Schemas['AuthorizationDelegationBlockReasonResponse'];
export type DelegationSignatureResponse = Schemas['AuthorizationDelegationSignatureResponse'];
export type MyDelegationResponse = Schemas['AuthorizationMyDelegationResponse'];

export type DelegationStatus =
  | 'Draft'
  | 'PendingAcceptance'
  | 'Active'
  | 'SuspendedCertification'
  | 'SuspendedLink'
  | 'Revoked'
  | 'Expired'
  | 'Superseded';

/** Em curso: uma só por (estudo, pessoa). */
export const CURRENT_STATUSES: ReadonlySet<string> = new Set([
  'Active',
  'SuspendedCertification',
  'SuspendedLink',
]);
/** Pendente de assinatura ou aceite: uma só por (estudo, pessoa). */
export const PENDING_STATUSES: ReadonlySet<string> = new Set(['Draft', 'PendingAcceptance']);
export const TERMINAL_STATUSES: ReadonlySet<string> = new Set(['Revoked', 'Expired', 'Superseded']);
/** Estudos que aceitam delegações (`StudySiteInfo.AcceptsDelegations`). */
export const OPEN_STUDY_STATUSES: ReadonlySet<string> = new Set(['Startup', 'Ready', 'Active']);

export interface FakeStudy {
  readonly id: string;
  readonly tenantId: string;
  /** Estudo do protocolo (os certificados de `PROTOCOL_TRAINING` são por estudo). */
  readonly studyId: string;
  readonly protocolCode: string;
  readonly title: string;
  readonly status: string;
  readonly principalInvestigatorPersonId: string;
}

export interface FakeDelegationLog {
  readonly id: string;
  readonly studySiteId: string;
  readonly tenantId: string;
  /** Revisões do documento: cada rascunho ou emenda, cada ativação e cada revogação de delegação em curso. */
  currentVersion: number;
}

export interface FakeDelegation {
  readonly id: string;
  readonly tenantId: string;
  readonly studySiteId: string;
  readonly delegationLogId: string;
  readonly version: number;
  readonly previousDelegationId: string | null;
  readonly personId: string;
  userId: string | null;
  readonly functionCode: string;
  readonly studyRoleCode: string | null;
  readonly tasks: { taskCode: string; requiredCertificationTypes: string[] }[];
  readonly effectiveFrom: string;
  readonly effectiveTo: string | null;
  status: DelegationStatus;
  statusReason: string | null;
  readonly contentHash: string;
  piSignature: DelegationSignatureResponse | null;
  acceptance: DelegationSignatureResponse | null;
  activatedAt: string | null;
  endedAt: string | null;
  endedBy: string | null;
  roleAssignmentId: string | null;
  readonly createdBy: string | null;
  readonly createdAt: string;
  rowVersion: number;
}

export interface FakeDelegationState {
  readonly studies: FakeStudy[];
  readonly logs: FakeDelegationLog[];
  readonly delegations: FakeDelegation[];
  readonly attempts: DelegationBlockAttemptResponse[];
  counter: number;
}

// ---- Identificadores ----

export const DELEGATION_IDS = {
  /** Usuário do investigador principal do estudo piloto (cenário `tenant-investigator`). */
  piUser: '01926f3e-0000-7000-8000-0000000000u8',
  /** Pessoa dele (`/me.person.id`), cadastrada como PI do estudo piloto. */
  piPerson: '01926f3e-0000-7000-8000-0000000000p8',
  /** PI do segundo estudo (Helena, do estado falso de Pessoas) e o usuário que assinou a delegação pendente. */
  secondPiPerson: PEOPLE_IDS.helena,
  secondPiUser: '01926f3e-0000-7000-8000-00000000a0e1',
  /** Pessoas que o investigador delega (semeadas em Pessoas só nos cenários do delegation log). */
  natalia: peopleId('person', 20),
  otavio: peopleId('person', 21),
  gabriel: peopleId('person', 22),
  nataliaLink: peopleId('link', 20),
  otavioLink: peopleId('link', 21),
  gabrielLink: peopleId('link', 22),
  /** Delegações semeadas. */
  draftGabriel: '01926f3e-0000-7000-8000-0000d1000001',
  activeOtavio: '01926f3e-0000-7000-8000-0000d1000002',
  revokedNatalia: '01926f3e-0000-7000-8000-0000d1000003',
  activeDelegate: '01926f3e-0000-7000-8000-0000d1000004',
  pendingDelegate: '01926f3e-0000-7000-8000-0000d1000005',
  logPilot: '01926f3e-0000-7000-8000-0000d2000001',
  logSecond: '01926f3e-0000-7000-8000-0000d2000002',
} as const;

export function fakeDelegationId(state: Pick<FakeDelegationState, 'counter'>): string {
  state.counter += 1;
  return `01926f3e-0000-7000-8000-0000d3${state.counter.toString(16).padStart(6, '0')}`;
}

/** ETag fraco de uma delegação (`RowVersion`). */
export function delegationEtag(delegation: Pick<FakeDelegation, 'rowVersion'>): string {
  return `W/"${delegation.rowVersion.toString(16).padStart(16, '0')}"`;
}

// ---- Hash do conteúdo (fake: SHA-256 não está disponível de forma síncrona; 64 hexadecimais determinísticos) ----

function mix(text: string, seed: number): number {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let index = 0; index < text.length; index += 1) {
    const ch = text.charCodeAt(index);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  return h1 >>> 0;
}

/** Hash determinístico (64 hexadecimais) do conteúdo canônico da delegação, como o `ContentHash` do servidor. */
export function contentHashOf(content: {
  readonly studySiteId: string;
  readonly tenantId: string;
  readonly version: number;
  readonly previousDelegationId: string | null;
  readonly personId: string;
  readonly functionCode: string;
  readonly tasks: readonly { taskCode: string; requiredCertificationTypes: readonly string[] }[];
  readonly effectiveFrom: string;
  readonly effectiveTo: string | null;
}): string {
  const canonical = JSON.stringify({
    ...content,
    tasks: [...content.tasks]
      .sort((a, b) => a.taskCode.localeCompare(b.taskCode))
      .map((task) => ({
        taskCode: task.taskCode,
        requiredCertificationTypes: [...task.requiredCertificationTypes].sort(),
      })),
  });
  let out = '';
  for (let seed = 0; seed < 8; seed += 1) {
    out += mix(canonical, seed).toString(16).padStart(8, '0');
  }
  return out;
}

// ---- Catálogo de tarefas (Anexo A.4 → códigos do Anexo E.3; permissões habilitadas abreviadas no falso) ----

const GCP = 'GCP_ICH_E6';
const ONBOARDING = 'ACADEMIA_ONBOARDING';
const IATA = 'IATA_SAMPLES';
const PROTOCOL = 'PROTOCOL_TRAINING';

function task(
  code: string,
  name: string,
  functions: string[],
  certifications: string[],
  license: boolean,
  permissions: string[],
  skills: string[] = [],
): DelegationTaskTypeResponse {
  return {
    code,
    name,
    allowedFunctionCodes: functions,
    requiredCertificationTypeCodes: certifications,
    requiresProfessionalLicense: license,
    enablesPermissions: permissions,
    requiredSkillCodes: skills,
    status: 'Active',
  };
}

export const TASK_TYPES: readonly DelegationTaskTypeResponse[] = [
  task(
    'AE_REPORTING_SUPPORT',
    'Apoio à notificação de eventos adversos',
    ['LEAD_STUDY_COORDINATOR', 'STUDY_COORDINATOR', 'SUB_INVESTIGATOR'],
    [GCP, PROTOCOL],
    false,
    [],
  ),
  task(
    'CLINICAL_ASSESSMENT',
    'Avaliação clínica',
    ['PRINCIPAL_INVESTIGATOR', 'SUB_INVESTIGATOR', 'EVALUATING_PHYSICIAN', 'NURSE'],
    [GCP, PROTOCOL],
    true,
    ['appointment.execute'],
  ),
  task(
    'DATA_ENTRY',
    'Entrada de dados',
    ['LEAD_STUDY_COORDINATOR', 'STUDY_COORDINATOR', 'DATA_ENTRY'],
    [GCP],
    false,
    ['participant.anchor.record', 'participant.read'],
  ),
  task(
    'DOCUMENT_ARCHIVING',
    'Arquivamento de documentos',
    ['LEAD_STUDY_COORDINATOR', 'STUDY_COORDINATOR'],
    [GCP],
    false,
    ['document.read', 'document.upload'],
  ),
  task(
    'ECG',
    'Eletrocardiograma (ECG)',
    ['NURSE', 'NURSING_TECHNICIAN'],
    [GCP],
    false,
    ['appointment.execute'],
    ['ECG_12_LEAD'],
  ),
  task(
    'ELIGIBILITY_ASSESSMENT',
    'Avaliação de elegibilidade',
    ['PRINCIPAL_INVESTIGATOR', 'SUB_INVESTIGATOR', 'EVALUATING_PHYSICIAN'],
    [GCP, PROTOCOL],
    true,
    ['participant.manage'],
  ),
  task(
    'INFORMED_CONSENT',
    'Consentimento livre e esclarecido',
    ['PRINCIPAL_INVESTIGATOR', 'SUB_INVESTIGATOR', 'LEAD_STUDY_COORDINATOR', 'STUDY_COORDINATOR'],
    [GCP, ONBOARDING, PROTOCOL],
    false,
    ['appointment.execute', 'participant.consent.record'],
  ),
  task(
    'OPERATIONAL_REVIEW',
    'Revisão operacional',
    ['LEAD_STUDY_COORDINATOR', 'OPERATIONS_LEAD'],
    [GCP],
    false,
    ['study.config.review.operational'],
  ),
  task(
    'PHLEBOTOMY',
    'Coleta de sangue (flebotomia)',
    ['NURSE', 'NURSING_TECHNICIAN', 'BIOMEDICAL', 'LAB_TECHNICIAN'],
    [GCP, ONBOARDING],
    false,
    ['appointment.execute'],
  ),
  task(
    'PRESCRIPTION_DISPENSING',
    'Prescrição e dispensação',
    ['PRINCIPAL_INVESTIGATOR', 'SUB_INVESTIGATOR', 'EVALUATING_PHYSICIAN', 'PHARMACIST_RT'],
    [GCP, PROTOCOL],
    true,
    ['appointment.execute', 'kit.manage'],
  ),
  task(
    'SAMPLE_PROCESSING_SHIPPING',
    'Processamento e envio de amostras',
    ['BIOMEDICAL', 'LAB_TECHNICIAN'],
    [IATA],
    false,
    ['appointment.execute', 'serviceorder.manage'],
  ),
  task(
    'SCHEDULING_COORDINATION',
    'Coordenação de agendamento',
    ['LEAD_STUDY_COORDINATOR', 'STUDY_COORDINATOR'],
    [GCP, ONBOARDING],
    false,
    ['appointment.book', 'participant.read', 'participant.manage', 'serviceorder.manage'],
  ),
  task(
    'STUDY_SETUP',
    'Preparação do estudo (start-up)',
    ['STARTUP_SPECIALIST', 'LEAD_STUDY_COORDINATOR'],
    [GCP, ONBOARDING],
    false,
    ['study.config.edit', 'study.import', 'study.readiness.manage'],
  ),
  task(
    'VITAL_SIGNS',
    'Sinais vitais',
    ['NURSE', 'NURSING_TECHNICIAN', 'BIOMEDICAL'],
    [GCP, ONBOARDING],
    false,
    ['appointment.execute'],
  ),
];

/** Mapa função → papel de estudo que a delegação ativa materializa (`DelegationRoleMap`, OPEN-107). */
export const ROLE_MAP: Readonly<Record<string, string>> = {
  PRINCIPAL_INVESTIGATOR: 'SUB_INVESTIGATOR',
  SUB_INVESTIGATOR: 'SUB_INVESTIGATOR',
  EVALUATING_PHYSICIAN: 'CLINICAL_STAFF',
  LEAD_STUDY_COORDINATOR: 'LEAD_COORDINATOR',
  STUDY_COORDINATOR: 'STUDY_COORDINATOR',
  PHARMACIST_RT: 'PHARMACIST_RT',
  PHARMACIST_BACKUP: 'PHARMACIST_RT',
  NURSE: 'CLINICAL_STAFF',
  NURSING_TECHNICIAN: 'CLINICAL_STAFF',
  BIOMEDICAL: 'CLINICAL_STAFF',
  LAB_TECHNICIAN: 'CLINICAL_STAFF',
  DATA_ENTRY: 'DATA_ENTRY',
};

// ---- Travas de compliance (REQ-AUTHZ-007; OPEN-110): puras sobre o estado falso de Pessoas ----

export interface LockInput {
  readonly tenantId: string;
  readonly studyId: string;
  readonly personId: string;
  readonly functionCode: string;
  readonly taskCodes: readonly string[];
  readonly evaluationDate: string;
}

function constraint(
  personId: string,
  code: string,
  detail: string,
  extra: { certificationTypeCode?: string; taskCode?: string; councilCode?: string } = {},
): DelegationBlockReasonResponse & { resourceType: string; resourceId: string } {
  return {
    code,
    detail,
    certificationTypeCode: extra.certificationTypeCode ?? null,
    taskCode: extra.taskCode ?? null,
    councilCode: extra.councilCode ?? null,
    resourceType: 'Person',
    resourceId: personId,
  };
}

/**
 * As cinco travas, sem curto-circuito e na ordem do servidor: (a) vínculo, (b) certificações por tipo, (c) licenças por conselho,
 * (d) função × tarefa, (d') skills por tarefa. Vazio = a pessoa passa.
 */
export function evaluateLocks(people: FakePeopleState, input: LockInput) {
  const requested = input.taskCodes
    .map((code) => TASK_TYPES.find((type) => type.code === code))
    .filter((type): type is DelegationTaskTypeResponse => type !== undefined);
  const date = input.evaluationDate;
  const result: ReturnType<typeof constraint>[] = [];

  // (a) vínculo ativo na função pedida, com a PJ do tenant ou com a holding.
  const linked = people.links.some(
    (link) =>
      link.personId === input.personId &&
      linkStatusOn(link, date) === 'Active' &&
      (link.tenantId === null || link.tenantId === input.tenantId) &&
      link.functionCode === input.functionCode,
  );
  if (!linked) {
    result.push(
      constraint(
        input.personId,
        'LINK_NOT_ACTIVE',
        `No valid link in function ${input.functionCode} on ${date}`,
      ),
    );
  }

  // (b) certificação vigente (e verificada) de cada tipo exigido pela união das tarefas.
  const types = [
    ...new Set(requested.flatMap((type) => type.requiredCertificationTypeCodes)),
  ].sort();
  for (const typeCode of types) {
    const valid = people.certifications.some(
      (certification) =>
        certification.personId === input.personId &&
        certification.typeCode === typeCode &&
        certification.revokedAt === null &&
        certification.issuedAt <= date &&
        certification.validUntil >= date &&
        certification.verifiedAt !== null &&
        (typeCode !== 'PROTOCOL_TRAINING' || certification.studyId === input.studyId),
    );
    if (!valid) {
      result.push(
        constraint(
          input.personId,
          'CERTIFICATION_MISSING',
          `No valid ${typeCode} certification on ${date}`,
          { certificationTypeCode: typeCode },
        ),
      );
    }
  }

  // (c) licença por conselho que a função exige; sem conselho na função, um motivo por tarefa que exige licença.
  const councils = [
    ...new Set(FUNCTIONS.find((item) => item.code === input.functionCode)?.requiredLicenseCouncils),
  ].sort();
  for (const council of councils) {
    const valid = people.licenses.some(
      (license) =>
        license.personId === input.personId &&
        license.council === council &&
        license.validUntil >= date,
    );
    if (!valid) {
      result.push(
        constraint(input.personId, 'LICENSE_MISSING', `No valid ${council} license on ${date}`, {
          councilCode: council,
        }),
      );
    }
  }
  if (councils.length === 0) {
    for (const type of requested.filter((item) => item.requiresProfessionalLicense)) {
      result.push(
        constraint(
          input.personId,
          'LICENSE_MISSING',
          `Task ${type.code} requires a professional license but function ${input.functionCode} defines no registration board`,
          { taskCode: type.code },
        ),
      );
    }
  }

  // (d) a função admite a tarefa.
  for (const type of [...requested].sort((a, b) => a.code.localeCompare(b.code))) {
    if (!type.allowedFunctionCodes.includes(input.functionCode)) {
      result.push(
        constraint(
          input.personId,
          'FUNCTION_NOT_ALLOWED',
          `Function ${input.functionCode} is not allowed to perform task ${type.code}`,
          { taskCode: type.code },
        ),
      );
    }
  }

  // (d') a capacidade (skill) que a tarefa exige.
  const held = new Set(
    people.skills
      .filter((skill) => skill.personId === input.personId)
      .map((skill) => skill.skillCode),
  );
  for (const type of [...requested].sort((a, b) => a.code.localeCompare(b.code))) {
    const missing = type.requiredSkillCodes.filter((skill) => !held.has(skill)).sort();
    if (missing.length > 0) {
      result.push(
        constraint(
          input.personId,
          'SKILL_MISSING',
          `Missing skill ${missing.join(', ')} for task ${type.code}`,
          { taskCode: type.code },
        ),
      );
    }
  }
  return result;
}

// ---- Conversões para o contrato ----

export function toDelegationResponse(
  delegation: FakeDelegation,
  people: FakePeopleState,
  today: string,
): DelegationResponse {
  const person = people.people.find((item) => item.id === delegation.personId);
  const { rowVersion: _rowVersion, tenantId: _tenantId, tasks, ...rest } = delegation;
  return {
    ...rest,
    personDisplayName: person ? displayNameOf(person) : null,
    tasks: [...tasks]
      .sort((a, b) => a.taskCode.localeCompare(b.taskCode))
      .map((item) => ({
        taskCode: item.taskCode,
        name: TASK_TYPES.find((type) => type.code === item.taskCode)?.name ?? null,
        requiredCertificationTypes: [...item.requiredCertificationTypes],
      })),
    effectiveStatus:
      delegation.status === 'Active' && delegation.effectiveFrom > today
        ? 'Scheduled'
        : delegation.status,
  };
}

// ---- Semente ----

function signature(
  userId: string,
  personId: string,
  signedAt: string,
  meaning: 'Delegate' | 'Accept',
): DelegationSignatureResponse {
  return { signedBy: userId, signerPersonId: personId, signedAt, meaning, comment: null };
}

function studyList(): [FakeStudy, FakeStudy, FakeStudy] {
  return [
    {
      id: FIXTURE_IDS.studyPilot,
      tenantId: FIXTURE_IDS.tenantPilot,
      studyId: '01926f3e-0000-7000-8000-00000051d101',
      // TODO(GIY-118): o protocolo e o título vêm do cadastro de estudos (M10); no falso, valores de exemplo.
      protocolCode: 'GIY-PILOT-001',
      title: 'Estudo piloto de hipertensão',
      status: 'Active',
      principalInvestigatorPersonId: DELEGATION_IDS.piPerson,
    },
    {
      id: FIXTURE_IDS.studyPilotSecond,
      tenantId: FIXTURE_IDS.tenantPilot,
      studyId: '01926f3e-0000-7000-8000-00000051d102',
      protocolCode: 'GIY-PILOT-002',
      title: 'Estudo piloto de diabetes (em start-up)',
      status: 'Startup',
      principalInvestigatorPersonId: DELEGATION_IDS.secondPiPerson,
    },
    {
      id: FIXTURE_IDS.studyEmergency,
      tenantId: FIXTURE_IDS.tenantPilot,
      studyId: '01926f3e-0000-7000-8000-00000051d103',
      protocolCode: 'GIY-PILOT-003',
      title: 'Estudo em fechamento',
      status: 'Closed',
      principalInvestigatorPersonId: DELEGATION_IDS.secondPiPerson,
    },
  ];
}

/** Estudos que o principal alcança: os de papel de estudo em `/me` e aqueles de que é o PI cadastrado. */
export function accessibleStudyIds(me: MeResponse, studies: readonly FakeStudy[]): Set<string> {
  const result = new Set<string>();
  for (const context of me.contexts) {
    for (const role of context.effectiveRoles) {
      if (role.scopeType === 'Study') {
        result.add(role.scopeId);
      }
    }
  }
  const personId = me.person?.id;
  for (const study of studies) {
    if (personId && study.principalInvestigatorPersonId === personId) {
      result.add(study.id);
    }
  }
  return result;
}

interface DraftSeed {
  readonly id: string;
  readonly study: FakeStudy;
  readonly log: FakeDelegationLog;
  readonly version?: number;
  readonly previousDelegationId?: string | null;
  readonly personId: string;
  readonly userId?: string | null;
  readonly functionCode: string;
  readonly tasks: readonly string[];
  readonly from: string;
  readonly to: string | null;
  readonly status: DelegationStatus;
  readonly createdAt: string;
}

function seedDelegation(state: FakeDelegationState, input: DraftSeed): FakeDelegation {
  const tasks = input.tasks.map((code) => ({
    taskCode: code,
    requiredCertificationTypes: [
      ...(TASK_TYPES.find((type) => type.code === code)?.requiredCertificationTypeCodes ?? []),
    ],
  }));
  const version = input.version ?? 1;
  const previousDelegationId = input.previousDelegationId ?? null;
  const delegation: FakeDelegation = {
    id: input.id,
    tenantId: input.study.tenantId,
    studySiteId: input.study.id,
    delegationLogId: input.log.id,
    version,
    previousDelegationId,
    personId: input.personId,
    userId: input.userId ?? null,
    functionCode: input.functionCode,
    studyRoleCode: ROLE_MAP[input.functionCode] ?? null,
    tasks,
    effectiveFrom: input.from,
    effectiveTo: input.to,
    status: input.status,
    statusReason: null,
    contentHash: contentHashOf({
      studySiteId: input.study.id,
      tenantId: input.study.tenantId,
      version,
      previousDelegationId,
      personId: input.personId,
      functionCode: input.functionCode,
      tasks,
      effectiveFrom: input.from,
      effectiveTo: input.to,
    }),
    piSignature: null,
    acceptance: null,
    activatedAt: null,
    endedAt: null,
    endedBy: null,
    roleAssignmentId: null,
    createdBy: DELEGATION_IDS.piUser,
    createdAt: input.createdAt,
    rowVersion: 1,
  };
  state.delegations.push(delegation);
  return delegation;
}

/** Pessoas que o investigador delega: dentro da conformidade (vínculo, licença, GCP e onboarding) e sem a capacidade de ECG. */
function seedDelegatedPeople(seeder: Seeder): void {
  const pilot = FIXTURE_IDS.tenantPilot;
  const sede = FIXTURE_IDS.sitePilotSede;
  const people: readonly {
    id: string;
    link: string;
    given: string;
    family: string;
    seed: number;
    fn: string;
    council: string | null;
  }[] = [
    {
      id: DELEGATION_IDS.natalia,
      link: DELEGATION_IDS.nataliaLink,
      given: 'Natália',
      family: 'Prates',
      seed: 20,
      fn: 'NURSE',
      council: 'COREN',
    },
    {
      id: DELEGATION_IDS.otavio,
      link: DELEGATION_IDS.otavioLink,
      given: 'Otávio',
      family: 'Lins',
      seed: 21,
      fn: 'LAB_TECHNICIAN',
      council: null,
    },
    {
      id: DELEGATION_IDS.gabriel,
      link: DELEGATION_IDS.gabrielLink,
      given: 'Gabriel',
      family: 'Moura',
      seed: 22,
      fn: 'NURSING_TECHNICIAN',
      council: 'COREN',
    },
  ];
  for (const item of people) {
    const person = seedPerson(seeder, {
      id: item.id,
      given: item.given,
      family: item.family,
      cpfSeed: item.seed,
      birthYear: 1990,
      email: `${item.given.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')}@centro.example`,
    });
    seedLink(seeder, {
      id: item.link,
      person,
      legalEntityId: FIXTURE_IDS.legalEntityPilot,
      tenantId: pilot,
      linkType: 'CLT',
      functionCode: item.fn,
      fromDaysAgo: 400,
      siteId: sede,
    });
    if (item.council) {
      seedLicense(seeder, person, item.council, 'PR', `${item.council}-${String(item.seed)}`, 400);
    }
    seedCertification(seeder, person, GCP, 100, 900, true);
    seedCertification(seeder, person, ONBOARDING, 100, 600, true);
  }
}

/** A delegada do cenário `tenant-delegate` (a própria pessoa de `/me`): licença do conselho e onboarding em dia, para o aceite passar nas travas. */
function seedDelegateCompliance(seeder: Seeder, person: FakePerson): void {
  seedLicense(seeder, person, 'COREN', 'PR', 'COREN-555', 400);
  seedCertification(seeder, person, ONBOARDING, 100, 600, true);
}

export function seedDelegationState(me: MeResponse, people: FakePeopleState): FakeDelegationState {
  const studies = studyList();
  const state: FakeDelegationState = {
    studies,
    logs: [],
    delegations: [],
    attempts: [],
    counter: 0,
  };
  const today = todayUtc();
  const now = Date.now();
  const iso = (daysAgo: number): string => new Date(now - daysAgo * 86_400_000).toISOString();
  const [pilot, second] = studies;

  // Só os cenários que alcançam algum estudo do centro recebem o log (e as pessoas delegadas em Pessoas).
  if (accessibleStudyIds(me, studies).size === 0) {
    return state;
  }
  const seeder: Seeder = { state: people, today, createdAt: iso(90) };
  seedDelegatedPeople(seeder);

  const logPilot: FakeDelegationLog = {
    id: DELEGATION_IDS.logPilot,
    studySiteId: pilot.id,
    tenantId: pilot.tenantId,
    currentVersion: 6,
  };
  const logSecond: FakeDelegationLog = {
    id: DELEGATION_IDS.logSecond,
    studySiteId: second.id,
    tenantId: second.tenantId,
    currentVersion: 1,
  };
  state.logs.push(logPilot, logSecond);

  const revoked = seedDelegation(state, {
    id: DELEGATION_IDS.revokedNatalia,
    study: pilot,
    log: logPilot,
    personId: DELEGATION_IDS.natalia,
    functionCode: 'NURSE',
    tasks: ['VITAL_SIGNS'],
    from: shiftDays(today, -120),
    to: null,
    status: 'Revoked',
    createdAt: iso(121),
  });
  revoked.piSignature = signature(
    DELEGATION_IDS.piUser,
    DELEGATION_IDS.piPerson,
    iso(120),
    'Delegate',
  );
  revoked.acceptance = signature(
    '01926f3e-0000-7000-8000-00000000a0e2',
    DELEGATION_IDS.natalia,
    iso(119),
    'Accept',
  );
  revoked.activatedAt = iso(119);
  revoked.endedAt = iso(10);
  revoked.endedBy = DELEGATION_IDS.piUser;
  revoked.statusReason = 'Fim da participação da profissional no estudo.';

  const active = seedDelegation(state, {
    id: DELEGATION_IDS.activeOtavio,
    study: pilot,
    log: logPilot,
    personId: DELEGATION_IDS.otavio,
    functionCode: 'LAB_TECHNICIAN',
    tasks: ['PHLEBOTOMY'],
    from: shiftDays(today, -30),
    to: null,
    status: 'Active',
    createdAt: iso(32),
  });
  active.piSignature = signature(
    DELEGATION_IDS.piUser,
    DELEGATION_IDS.piPerson,
    iso(31),
    'Delegate',
  );
  active.acceptance = signature(
    '01926f3e-0000-7000-8000-00000000a0e3',
    DELEGATION_IDS.otavio,
    iso(30),
    'Accept',
  );
  active.activatedAt = iso(30);

  seedDelegation(state, {
    id: DELEGATION_IDS.draftGabriel,
    study: pilot,
    log: logPilot,
    personId: DELEGATION_IDS.gabriel,
    functionCode: 'NURSING_TECHNICIAN',
    tasks: ['VITAL_SIGNS'],
    from: today,
    to: null,
    status: 'Draft',
    createdAt: iso(1),
  });

  if (me.person?.id === FIXTURE_DELEGATION.delegatePerson) {
    const own = people.people.find((item) => item.id === FIXTURE_DELEGATION.delegatePerson);
    if (own) {
      seedDelegateCompliance(seeder, own);
    }
    const mine = seedDelegation(state, {
      id: DELEGATION_IDS.activeDelegate,
      study: pilot,
      log: logPilot,
      version: 1,
      personId: FIXTURE_DELEGATION.delegatePerson,
      userId: FIXTURE_DELEGATION.delegateUser,
      functionCode: 'NURSE',
      tasks: ['VITAL_SIGNS'],
      from: shiftDays(today, -20),
      to: null,
      status: 'Active',
      createdAt: iso(22),
    });
    mine.piSignature = signature(
      DELEGATION_IDS.piUser,
      DELEGATION_IDS.piPerson,
      iso(21),
      'Delegate',
    );
    mine.acceptance = signature(
      FIXTURE_DELEGATION.delegateUser,
      FIXTURE_DELEGATION.delegatePerson,
      iso(20),
      'Accept',
    );
    mine.activatedAt = iso(20);
    mine.roleAssignmentId = '01926f3e-0000-7000-8000-0000d4000001';
    logPilot.currentVersion += 2;

    const pending = seedDelegation(state, {
      id: DELEGATION_IDS.pendingDelegate,
      study: second,
      log: logSecond,
      personId: FIXTURE_DELEGATION.delegatePerson,
      userId: FIXTURE_DELEGATION.delegateUser,
      functionCode: 'NURSE',
      tasks: ['PHLEBOTOMY'],
      from: today,
      to: shiftDays(today, 365),
      status: 'PendingAcceptance',
      createdAt: iso(2),
    });
    pending.piSignature = signature(
      DELEGATION_IDS.secondPiUser,
      DELEGATION_IDS.secondPiPerson,
      iso(1),
      'Delegate',
    );
  }
  return state;
}
